import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {PeriodicCheckPayload, PeriodicCheck, PeriodicChecksPayload, Machine, LastPeriodicCheck, Project, PeriodicCheckDraftResponse, PeriodicCheckSaveResponse} from '~/types';
import {StoreModel} from "~/store";
import axios from "axios";
import {API} from "~/app/routes";
import { jsonToFormData } from '~/app/helpers/headerContentType';
import { generateGUID } from '~/app/helpers/guid';
import { PeriodicCheckOfflineService } from '~/sqlite/services/periodicCheck';
import { buildChecksForSave, parsePeriodicCheckImages } from '~/periodicCheck/helpers';
export type PeriodicCheckEquipment = Pick<Machine, 'id' | 'intern' | 'name'>

interface ChecksDataPayload {
    inspectionType: Machine['inspectionType']
    checks?: PeriodicCheck[]
    draftChecks?: PeriodicCheckPayload[]
    dailyCheckId?: string | null
    lastRegisteredCheck: LastPeriodicCheck | undefined
    equipment?: PeriodicCheckEquipment
    projectId?: Project['id']
}

export enum InspectionTypes {
    DAILY = 0,
    PERIODIC = 1,
    ACCEPTANCE_INSPECTION = 2
}

const mergeCheckPayload = (
    template: PeriodicCheck | PeriodicCheckPayload,
    draft?: PeriodicCheckPayload,
): PeriodicCheckPayload => {
    const base = template as PeriodicCheckPayload;
    const baseParsed = parsePeriodicCheckImages(base.images, base.im);
    const draftParsed = draft
        ? parsePeriodicCheckImages(draft.images ?? base.images, draft.im ?? base.im)
        : baseParsed;

    return {
        ...template,
        status: draft?.status ?? base.status ?? 0,
        issues: draft?.issues ?? base.issues ?? [],
        quantity: draft?.quantity ?? base.quantity ?? 0,
        images: draft ? draftParsed.images : baseParsed.images,
        im: draft ? draftParsed.im : baseParsed.im,
    };
};

const buildSavePayload = (
    state: {
        dailyCheckId: string | null
        checkEquipment: PeriodicCheckEquipment | null
        inspectionType: Machine['inspectionType']
        checksData: PeriodicCheckPayload[]
        projectId: Project['id'] | null
    },
    payload: PeriodicChecksPayload,
    machineId: Machine['id'],
): PeriodicChecksPayload => {
    const dailyCheckId = state.dailyCheckId ?? null;
    return {
        ...payload,
        machineId: payload.machineId ?? machineId,
        projectId: payload.projectId ?? state.projectId!,
        inspectionType: payload.inspectionType ?? state.inspectionType,
        checks: payload.checks ?? state.checksData,
        guid: payload.guid ?? generateGUID(),
        isDraft: payload.isDraft ?? 0,
        ...(dailyCheckId && { id: dailyCheckId }),
    };
};

export interface PeriodicCheckModel {
    checksData: PeriodicCheckPayload[]
    inspectionType: Machine['inspectionType']
    lastRegisteredCheck: ChecksDataPayload['lastRegisteredCheck']
    checkEquipment: PeriodicCheckEquipment | null
    projectId: Project['id'] | null
    dailyCheckId: string | null
    setDailyCheckId: Action<PeriodicCheckModel, string | null>
    clearDraft: Action<PeriodicCheckModel>

    currentCheck: PeriodicCheckPayload | null
    updateCurrentCheck: Action<PeriodicCheckModel, PeriodicCheckPayload | null>

    loadChecksData: Action<PeriodicCheckModel, ChecksDataPayload>
    applyCheckData: Action<PeriodicCheckModel, PeriodicCheckDraftResponse>
    updateCheckData: Action<PeriodicCheckModel, PeriodicCheckPayload>
    isChecksDataUpdated: Computed<PeriodicCheckModel>

    updating: boolean
    setUpdating: Action<PeriodicCheckModel, boolean>
    update: Thunk<PeriodicCheckModel, PeriodicChecksPayload, any, StoreModel, Promise<PeriodicCheckSaveResponse | void>>
    setOfflinePeriodicChecksData: Thunk<PeriodicCheckModel, PeriodicChecksPayload, any, StoreModel, any>
}

export const periodicCheckModel: PeriodicCheckModel = {
    checksData: [],
    inspectionType: 0,
    lastRegisteredCheck: undefined,
    checkEquipment: null,
    projectId: null,
    dailyCheckId: null,
    setDailyCheckId: action((state, id) => {
        state.dailyCheckId = id;
    }),
    clearDraft: action((state) => {
        state.dailyCheckId = null;
    }),

    currentCheck: null,
    updateCurrentCheck: action((state, check) => {
        state.currentCheck = check;
    }),

    loadChecksData: action((state, data) => {
        const t = Number(data.inspectionType);
        state.inspectionType =
            t === InspectionTypes.DAILY ||
            t === InspectionTypes.PERIODIC ||
            t === InspectionTypes.ACCEPTANCE_INSPECTION
                ? t
                : InspectionTypes.DAILY;
        state.lastRegisteredCheck = data.lastRegisteredCheck;
        state.checkEquipment = data.equipment ?? null;
        state.dailyCheckId = data.dailyCheckId ?? null;
        state.checksData = [];
        const draftById = new Map( (data.draftChecks ?? []).map(check => [String(check.id), check]));
        (data.checks ?? []).forEach(check => {
            state.checksData.push(mergeCheckPayload(check, draftById.get(String(check.id))));
        });
    }),
    applyCheckData: action((state, checkData) => {
        if (checkData?.id != null && checkData.id !== '') {
            state.dailyCheckId = String(checkData.id);
        }
        if (!checkData?.checks?.length) {
            return;
        }
        const draftById = new Map(
            checkData.checks.map(check => [String(check.id), check])
        );
        state.checksData = state.checksData.map(item => {
            const draft = draftById.get(String(item.id));
            return draft ? mergeCheckPayload(item, draft) : item;
        });
    }),
    updateCheckData: action((state, check) => {
        state.checksData = state.checksData.map(item =>
            String(item.id) === String(check.id) ? check : item
        );
    }),
    isChecksDataUpdated: computed(state =>
        state.checksData
            .filter(check => check.isRequired)
            .every(check => check.status !== 0)
    ),

    updating: false,
    setUpdating: action((state, payload) => {
        state.updating = payload;
    }),
    update: thunk(async (actions, payload, { getState, getStoreState }) => {
        const state = getState();
        const machineId = getStoreState().machine.data.id;
        const savePayload = buildSavePayload(state, payload, machineId);
        const serializedPayload = {
            ...savePayload,
            checks: buildChecksForSave(savePayload.checks),
        };
        actions.setUpdating(true);
        try {
        const response = await axios.post<PeriodicCheckSaveResponse>(`${API.periodicCheck}/save`,
                jsonToFormData(serializedPayload)
            ) as PeriodicCheckSaveResponse;
            if (response?.checkData) {
                actions.applyCheckData(response.checkData);
            }
            if (savePayload.isDraft) {
                const draftId = response?.checkData?.id ?? response?.id;
                if (draftId != null && draftId !== '') {
                    actions.setDailyCheckId(String(draftId));
                }
            }
            if (!savePayload.isDraft) {
                actions.clearDraft();
            }
            return response;
        } finally {
            actions.setUpdating(false);
        }
    }),
    setOfflinePeriodicChecksData: thunk(async (actions, payload) => {
        const PeriodicCheckOffline = new PeriodicCheckOfflineService();
        return await PeriodicCheckOffline.updatePeriodicChecks(payload);
    })
};
